import { useState, useRef } from 'react'
import { useRecipes } from '../hooks/useRecipes'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

const IngredientRow = ({ ing, onChange, onRemove }) => (
  <div className="flex gap-1.5 items-center min-w-0">
    <input
      type="text"
      placeholder="Amt"
      value={ing.amount ?? ''}
      onChange={(e) => onChange({ ...ing, amount: e.target.value })}
      className="w-14 min-w-0 rounded-lg border border-border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
    />
    <input
      type="text"
      placeholder="Unit"
      value={ing.unit ?? ''}
      onChange={(e) => onChange({ ...ing, unit: e.target.value })}
      className="w-14 min-w-0 rounded-lg border border-border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
    />
    <input
      type="text"
      placeholder="Ingredient name"
      value={ing.name ?? ''}
      onChange={(e) => onChange({ ...ing, name: e.target.value })}
      className="flex-1 min-w-0 rounded-lg border border-border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
    />
    <button type="button" onClick={onRemove} className="text-text-muted hover:text-rose-500 flex-shrink-0">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </button>
  </div>
)

const StepRow = ({ index, text, onChange, onRemove }) => (
  <div className="flex gap-2 items-start">
    <span className="text-xs font-bold text-primary bg-primary-light w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-1.5">
      {index + 1}
    </span>
    <textarea
      placeholder={`Step ${index + 1}…`}
      value={text}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      className="flex-1 rounded-lg border border-border px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
    />
    <button type="button" onClick={onRemove} className="text-text-muted hover:text-rose-500 flex-shrink-0 mt-1.5">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </button>
  </div>
)

const emptyIng  = () => ({ name: '', amount: '', unit: '' })
const emptyStep = () => ''

export const RecipeForm = ({ recipe, onClose }) => {
  const { createRecipe, updateRecipe, scrapeRecipe } = useRecipes()
  const editing = !!recipe

  const [title,       setTitle]       = useState(recipe?.title            ?? '')
  const [description, setDescription] = useState(recipe?.description      ?? '')
  const [prepTime,    setPrepTime]    = useState(recipe?.prep_time_minutes ?? '')
  const [cookTime,    setCookTime]    = useState(recipe?.cook_time_minutes ?? '')
  const [servings,    setServings]    = useState(recipe?.servings          ?? '')
  const [rating,      setRating]      = useState(recipe?.rating            ?? 0)
  const [sourceUrl,   setSourceUrl]   = useState(recipe?.source_url        ?? '')
  const [photoUrl,    setPhotoUrl]    = useState(recipe?.photo_url         ?? '')
  const [photoFile,   setPhotoFile]   = useState(null)
  const [photoPreview, setPhotoPreview] = useState(recipe?.photo_url ?? '')
  const [ingredients, setIngredients] = useState(recipe?.ingredients?.length ? recipe.ingredients : [emptyIng()])
  const [steps,       setSteps]       = useState(recipe?.steps?.length
    ? recipe.steps.map((s) => typeof s === 'string' ? s : s.text ?? s.instruction ?? '')
    : [emptyStep()])
  const [saving,      setSaving]      = useState(false)
  const [scraping,    setScraping]    = useState(false)
  const [importUrl,   setImportUrl]   = useState('')
  const [showImport,  setShowImport]  = useState(false)
  const [error,       setError]       = useState('')
  const fileRef = useRef()

  const handlePhoto = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  const handleImport = async () => {
    if (!importUrl.trim()) return
    setScraping(true)
    setError('')
    try {
      const data = await scrapeRecipe(importUrl.trim())
      if (data.title)        setTitle(data.title)
      if (data.description)  setDescription(data.description)
      if (data.prepTime)     setPrepTime(data.prepTime)
      if (data.cookTime)     setCookTime(data.cookTime)
      if (data.servings)     setServings(data.servings)
      if (data.sourceUrl)    setSourceUrl(data.sourceUrl)
      if (data.ingredients?.length) setIngredients(data.ingredients)
      if (data.steps?.length)       setSteps(data.steps.map((s) => typeof s === 'string' ? s : s.text ?? ''))
      setShowImport(false)
    } catch (err) {
      setError(`Import failed: ${err.message}`)
    } finally { setScraping(false) }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim()) { setError('Title is required'); return }
    setSaving(true)
    setError('')
    try {
      const payload = {
        title:              title.trim(),
        description:        description || null,
        prep_time_minutes:  prepTime ? parseInt(prepTime) : null,
        cook_time_minutes:  cookTime ? parseInt(cookTime) : null,
        servings:           servings ? parseInt(servings) : null,
        rating:             rating || null,
        source_url:         sourceUrl || null,
        photo_url:          photoUrl || null,
        ingredients:        ingredients.filter((i) => i.name.trim()),
        steps:              steps.filter((s) => s.trim()),
        photoFile,
      }
      editing ? await updateRecipe({ id: recipe.id, ...payload }) : await createRecipe(payload)
      onClose()
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const updateIng  = (i, val) => setIngredients((prev) => prev.map((x, j) => j === i ? val : x))
  const removeIng  = (i)      => setIngredients((prev) => prev.filter((_, j) => j !== i))
  const updateStep = (i, val) => setSteps((prev) => prev.map((x, j) => j === i ? val : x))
  const removeStep = (i)      => setSteps((prev) => prev.filter((_, j) => j !== i))

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-base font-bold text-text-primary">{editing ? 'Edit Recipe' : 'New Recipe'}</h2>
          <div className="flex gap-3">
            {!editing && (
              <button type="button" onClick={() => setShowImport((v) => !v)} className="text-sm text-primary font-medium">
                Import URL
              </button>
            )}
            <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary p-1">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Import URL bar */}
        {showImport && (
          <div className="px-5 py-3 border-b border-border bg-background flex gap-2">
            <input
              type="url"
              placeholder="Paste recipe URL…"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              className="flex-1 rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Button type="button" size="sm" onClick={handleImport} disabled={scraping || !importUrl.trim()}>
              {scraping ? <Spinner size="sm" /> : 'Import'}
            </Button>
          </div>
        )}

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-5">
          {/* Photo */}
          <div>
            <input type="file" accept="image/*" ref={fileRef} onChange={handlePhoto} className="hidden" />
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="" className="w-full h-40 object-cover rounded-xl" />
                <button
                  type="button"
                  onClick={() => { setPhotoPreview(''); setPhotoFile(null); setPhotoUrl('') }}
                  className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                >✕</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full h-32 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 text-text-muted hover:border-primary hover:text-primary transition-colors"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
                <span className="text-sm">Add photo</span>
              </button>
            )}
          </div>

          <input
            type="text"
            placeholder="Recipe title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <textarea
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div>
              <label className="text-xs font-semibold text-text-muted mb-1 block uppercase tracking-wide">Prep (min)</label>
              <input type="number" min="0" value={prepTime} onChange={(e) => setPrepTime(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text-muted mb-1 block uppercase tracking-wide">Cook (min)</label>
              <input type="number" min="0" value={cookTime} onChange={(e) => setCookTime(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text-muted mb-1 block uppercase tracking-wide">Servings</label>
              <input type="number" min="1" value={servings} onChange={(e) => setServings(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="text-xs font-semibold text-text-muted mb-2 block uppercase tracking-wide">Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" onClick={() => setRating(rating === i ? 0 : i)}>
                  <svg width="22" height="22" viewBox="0 0 24 24"
                    fill={i <= rating ? '#f59e0b' : 'none'}
                    stroke={i <= rating ? '#f59e0b' : '#d1d5db'}
                    strokeWidth="1.5"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <label className="text-xs font-semibold text-text-muted mb-2 block uppercase tracking-wide">Ingredients</label>
            <div className="space-y-2">
              {ingredients.map((ing, i) => (
                <IngredientRow key={i} ing={ing} onChange={(v) => updateIng(i, v)} onRemove={() => removeIng(i)} />
              ))}
            </div>
            <button type="button" onClick={() => setIngredients((p) => [...p, emptyIng()])}
              className="mt-2 text-sm text-primary font-medium flex items-center gap-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
              Add ingredient
            </button>
          </div>

          {/* Steps */}
          <div>
            <label className="text-xs font-semibold text-text-muted mb-2 block uppercase tracking-wide">Instructions</label>
            <div className="space-y-2">
              {steps.map((step, i) => (
                <StepRow key={i} index={i} text={step} onChange={(v) => updateStep(i, v)} onRemove={() => removeStep(i)} />
              ))}
            </div>
            <button type="button" onClick={() => setSteps((p) => [...p, emptyStep()])}
              className="mt-2 text-sm text-primary font-medium flex items-center gap-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
              Add step
            </button>
          </div>

          {sourceUrl && (
            <div>
              <label className="text-xs font-semibold text-text-muted mb-1 block uppercase tracking-wide">Source URL</label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving}>
            {saving ? <Spinner size="sm" /> : editing ? 'Save Recipe' : 'Create Recipe'}
          </Button>
        </div>
      </form>
    </div>
  )
}
